import Cookies from "js-cookie";
import {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {COOKIES_USER_ID, COOKIES_COMPANY_ID, METRICS_URL, SESSION_ID_KEY} from "../utils/constants";

type EventType =
| "execute.tool.opened"
| "execute.tool.closed"
| "execute.tool.loaded"
| "execute.tool.view_switched";
type TrackingEvent = {attributes: Object, session_id: string, event_type: EventType};

function ingest(event: TrackingEvent): void {
	const company_id = Cookies.get(COOKIES_COMPANY_ID);
    const user_id = Cookies.get(COOKIES_USER_ID);
    const data = Object.assign({company_id, user_id}, event);
    navigator.sendBeacon(METRICS_URL, JSON.stringify(data));
}

export default function usePageTracking(): void {
    const location = useLocation();
    const sessionStartRef = useRef<number>(Date.now());
    const pageEnterTimeRef = useRef<number>(Date.now());
    const prevPathRef = useRef<string>(location.pathname);
    const [sessionId, setSessionId] = useState<string|undefined>(undefined);
    const tool_name = "trailer-load";

    function getView() {
        return location.pathname.slice(1).replaceAll("/", ":") || undefined;
    }

    function reportLoad() {
        let duration
        let result;
        const [nav] = performance.getEntriesByType(
            "navigation"
        ) as PerformanceNavigationTiming[];
        if (!nav) {
            return;
        }
        if (nav.responseEnd === 0 && nav.transferSize === 0) {
            result = "failed";
        } else {
            result = "sucess";
        }
        duration = Math.round(nav.loadEventEnd - nav.startTime);
        ingest({
            attributes: {duration, result, tool_name, tool_subview: getView()},
            session_id: sessionId ?? "",
            event_type: "execute.tool.loaded"
        });
    }

    // Initial page open (once per session)
    useEffect(function () {
        let id = sessionStorage.getItem(SESSION_ID_KEY);
        if (!id) {
            id = crypto.randomUUID();
            sessionStorage.setItem(SESSION_ID_KEY, id);
        }
        setSessionId(id);
        if (!sessionStorage.getItem("s_page_opened")) {
            sessionStorage.setItem("s_page_opened", "true");
            ingest({
                attributes: {tool_name, tool_subview: getView()},
                session_id: sessionId ?? "",
                event_type: "execute.tool.opened"
            });
        }
    }, []);

    // Initial page load duration
    useEffect(function () {
        const [nav] = performance.getEntriesByType(
            "navigation"
        ) as PerformanceNavigationTiming[];
        // loadEventEnd is 0 when the load event hasn't fired yet
        if (nav && nav.loadEventEnd > 0) {
            reportLoad();
        } else {
            window.addEventListener("load", reportLoad, {once: true});
            return () => window.removeEventListener("load", reportLoad);
        }
    }, []);

    // Route change tracking
    useEffect(function () {
        const now = Date.now();
        const to = getView();
        let from = prevPathRef.current.slice(1).replaceAll("/", ":") || undefined;
        let duration;
        if (to !== from) {
            duration = now - pageEnterTimeRef.current;
            ingest({
                attributes: {from , to, tool_name, time_on_page: duration},
                session_id: sessionId ?? "",
                event_type: "execute.tool.view_switched",
            });
            prevPathRef.current = to ?? "";
            pageEnterTimeRef.current = now;
        }
    }, [location.pathname]);

    useEffect(function () {
        function handleBeforeUnload() {
            const now = Date.now();
            ingest({
                attributes: {
                    session_duration: now - sessionStartRef.current,
                    time_on_page: now - pageEnterTimeRef.current,
                    tool_name,
                    tool_subview: getView()
                },
                event_type: "execute.tool.closed",
                session_id: sessionId ?? ""
            });
        }
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);
}
